import type { ProductAddActionsProps } from './ProductAddActions';
import type { FC, RefObject } from 'react';

import styled, { css, useTheme } from 'styled-components';

import { media } from '~/components/shared/core/styles';
import { Typography } from '~/components/shared/core/typography/Typography';

const S = {
  ToolTipWrapper: styled.div<{
    $tooltipError: boolean;
    $outerClicked: boolean;
    $viewType: ProductAddActionsProps['viewType'];
  }>`
    display: ${({ $outerClicked }) => ($outerClicked ? 'none' : 'block')};
    position: absolute;
    visibility: ${({ $tooltipError }) => ($tooltipError ? 'visible' : 'hidden')};
    opacity: ${({ $tooltipError }) => ($tooltipError ? 1 : 0)};
    transition: visibility 0.1s linear, opacity 0.1s linear, transform 0.1s linear;
    left: 0;
    right: 0;
    top: -140px;
    z-index: 13;
    width: fit-content;
    margin: auto;

    @media ${({ theme }) => media(theme).greaterThan('lg')} {
      ${({ $viewType }) => $viewType === 'general view' && `inset: auto auto 0 -295px`};
    }

    @media (prefers-reduced-motion) {
      transition: none;
    }
  `,

  Tooltip: styled.div<{
    $viewType: ProductAddActionsProps['viewType'];
  }>`
    margin: auto;
    background-color: ${({ theme }) => theme.colors.neutral['0']};
    padding: 30px;
    width: 210px;
    box-shadow: 0 0 2px 2px rgba(0 0 0 / 5%);

    &::before {
      position: absolute;
      font-family: gstar-icons;
      content: '${'\\F111'}';
      inset: auto calc(50% - 7px) -10px auto;
      transform: rotate(90deg);
      color: ${({ theme }) => theme.colors.neutral['0']};
      font-size: 14px;
      height: 14px;
      width: 14px;

      @media ${({ theme }) => media(theme).greaterThan('lg')} {
        ${({ $viewType }) =>
          $viewType === 'general view' &&
          css`
            transform: none;
            right: -10px;
            top: 50px;
          `}
      }
    }
  `,

  ArrowRightIconWrapper: styled.div`
    height: 14px;
    width: 14px;
    position: absolute;
    right: -10px;
    bottom: 15px;
  `,

  SizeError: styled(Typography)`
    line-height: 20px;
    font-weight: ${({ theme }) => theme.variables.font.weight.regular};
  `,

  CartMutationError: styled(Typography)`
    line-height: 18px;
  `,
};

interface Props {
  viewType: ProductAddActionsProps['viewType'];
  cartMutationError: string;
  outerClicked: boolean;
  tooltipRef: RefObject<HTMLDivElement | null>;
  sizeError: boolean;
  sizeErrorText?: string;
}

export const CartErrorTooltip: FC<Props> = ({
  viewType,
  cartMutationError,
  outerClicked,
  tooltipRef,
  sizeError,
  sizeErrorText,
}) => {
  const theme = useTheme();

  return (
    <S.ToolTipWrapper
      $tooltipError={Boolean(sizeError || cartMutationError)}
      ref={tooltipRef}
      $outerClicked={outerClicked}
      $viewType={viewType}
    >
      <S.Tooltip $viewType={viewType} data-testid="add-to-cart-error">
        {sizeError && sizeErrorText && (
          <S.SizeError component="p" variant="subtitle" color={theme.colors.content.error}>
            {sizeErrorText}
          </S.SizeError>
        )}
        {cartMutationError && (
          <S.CartMutationError component="p" variant="body">
            {cartMutationError}
          </S.CartMutationError>
        )}
      </S.Tooltip>
    </S.ToolTipWrapper>
  );
};
